import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PlatformStorage} from '@app/core/tokens/platform-storage.token';
import {environment} from '@environments/environment';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, of, retry, switchMap, withLatestFrom} from 'rxjs';
import {StorageKeys} from 'src/app/core/constants/storage-keys';
import {IAuthRequest} from '../models/auth-request.model';
import {IAuthResponse} from '../models/auth-response.model';
import {EAccountConfirmationStatus, ELoginStatus} from '../models/auth-state.model';
import {AuthConfigService} from '../services/auth-config.service';
import {
  AuthActions,
  accountConfirmationFailure,
  accountConfirmationSuccess,
  authenticateFailure,
  authenticateSuccess,
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
  checkAccountConfirmation,
  forgotPassword,
  forgotPasswordFailure,
  forgotPasswordSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
} from './auth.actions';

import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {ELanguage} from '@app/shared/enums/language.enum';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {EStaticPage} from '@app/static-pages/enums/static-page.enum';
import {
  contactAction,
  cookiePolicyAction,
  legalNoticeAction,
  privacyPolicyAction,
  termsAndConditionsAction,
} from '@app/static-pages/state/static-pages.actions';
import {TranslateService} from '@ngx-translate/core';
import {AuthOptions, OidcSecurityService} from 'angular-auth-oidc-client';
import {MessageService} from 'primeng/api';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authConfigService = inject(AuthConfigService);
  private readonly storage = inject(PlatformStorage);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  static readonly MAX_AUTH_RETRIES = 2;
  static readonly GLOBAL_TOAST = 'global-toast';
  globalToast = 'global-toast';
  authenticate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.Authenticate),
      switchMap(() => this.preAuth())
    )
  );

  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.CheckAuth),
      switchMap(() => {
        if (this.authConfigService.isTokenValid()) {
          const accessToken = this.authConfigService.getAccessToken();
          return of(
            authenticateSuccess({
              token: accessToken,
              status: ELoginStatus.Authorized,
            })
          );
        }
        return this.preAuth();
      })
    )
  );

  preAuthForTermsAndCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.preAuthForTermsAndCondition),
      switchMap(() => this.preAuth()),
      switchMap(() => {
        const request = {
          key: EStaticPage.TermsAndConditions,
          languageId: ELanguage.EN,
        };
        return of(termsAndConditionsAction({request}));
      })
    )
  );

  preAuthForLegalNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.preAuthForLegalNotice),
      switchMap(() => this.preAuth()),
      switchMap(() => {
        const request = {
          key: EStaticPage.LegalNotice,
          languageId: ELanguage.EN,
        };
        return of(legalNoticeAction({request}));
      })
    )
  );

  preAuthForPrivacyPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.preAuthForPrivacyPolicy),
      switchMap(() => this.preAuth()),
      switchMap(() => {
        const request = {
          key: EStaticPage.PrivacyPolicy,
          languageId: ELanguage.EN,
        };
        return of(privacyPolicyAction({request}));
      })
    )
  );

  preAuthForCookiePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.preAuthForCookiePolicy),
      switchMap(() => this.preAuth()),
      switchMap(() => {
        const request = {
          key: EStaticPage.CookiePolicy,
          languageId: ELanguage.EN,
        };
        return of(cookiePolicyAction({request}));
      })
    )
  );

  preAuthForContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.preAuthForContact),
      switchMap(() => this.preAuth()),
      switchMap(() => {
        const request = {
          key: EStaticPage.Contact,
          languageId: ELanguage.EN,
        };
        return of(contactAction({request}));
      })
    )
  );

  preAuthForAccountConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.PreAuthForAccountConfirmation),
      withLatestFrom(this.activatedRoute.queryParams),
      switchMap(([, params]) => this.preAuth().pipe(map(action => ({params, action})))),
      switchMap(({params}) => {
        return of(
          checkAccountConfirmation({
            email: params['email'],
            token: decodeURIComponent(params['token']),
          })
        );
      })
    )
  );

  preAuthForLanguageLoader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.PreAuthForLanguageLoader),
      switchMap(() => this.preAuth())
    )
  );

  checkAccountConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.CheckAccountConfirmation),
      switchMap((action: ReturnType<typeof checkAccountConfirmation>) =>
        this.authConfigService.getAccountConfirmation(action.email, action.token)
      ),
      switchMap(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Auth.EmailSuccessfullyVerified'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return of(
          accountConfirmationSuccess({
            accountConfirmationStatus: EAccountConfirmationStatus.Confirmed,
          })
        );
      }),
      catchError(() =>
        of(
          accountConfirmationFailure({
            accountConfirmationStatus: EAccountConfirmationStatus.Error,
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResetPassword),
      switchMap((action: ReturnType<typeof resetPassword>) => {
        return this.resetPassword(action);
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ForgotPassword),
      switchMap((action: ReturnType<typeof forgotPassword>) => {
        return this.forgotPassword(action);
      })
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ChangePassword),
      switchMap((action: ReturnType<typeof changePassword>) => {
        return this.changePassword(action);
      })
    )
  );

  private preAuth() {
    return this.authConfigService.getPreAuth(this.getAuthRequest(environment.oidcConfig.authRequest)).pipe(
      map((response: IAuthResponse) => {
        if (response.access_token) {
          this.storage.setItem(StorageKeys.accessToken, response.access_token);
          return authenticateSuccess({
            token: response.access_token,
            status: ELoginStatus.Authorized,
          });
        }

        return authenticateFailure();
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return of(authenticateFailure());
      }),
      retry(AuthEffects.MAX_AUTH_RETRIES)
    );
  }

  private resetPassword(action: ReturnType<typeof resetPassword>) {
    return this.authConfigService.resetPassword(action.request).pipe(
      map(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Auth.ResetPasswordSuccess'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });

        return resetPasswordSuccess({
          resetPasswordStatus: EAccountConfirmationStatus.Confirmed,
        });
      }),
      catchError((errorResult: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(errorResult.status);
        this.showErrorMessage(errMsg);
        return of(
          resetPasswordFailure({
            resetPasswordStatus: EAccountConfirmationStatus.Error,
          })
        );
      })
    );
  }

  private forgotPassword(action: ReturnType<typeof forgotPassword>) {
    return this.authConfigService.forgotPassword(action.email).pipe(
      map(() => {
        const authOptions: AuthOptions = {
          customParams: {
            notificationType: 'success',
            notificationValue: this.translate.instant('Auth.ForgotPasswordSuccess'),
          },
        };
        this.storage.removeItem(StorageKeys.accessToken);
        this.oidcSecurityService.authorize(undefined, authOptions);
        return forgotPasswordSuccess({
          forgotPasswordStatus: EAccountConfirmationStatus.Confirmed,
        });
      }),
      catchError((errorResult: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(errorResult.status);
        this.showErrorMessage(errMsg);
        return of(
          forgotPasswordFailure({
            forgotPasswordStatus: EAccountConfirmationStatus.Error,
          })
        );
      })
    );
  }

  private changePassword(action: ReturnType<typeof changePassword>) {
    return this.authConfigService.changePassword(action.oldPassword, action.newPassword).pipe(
      map(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Profile.changePasswordSuccess'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return changePasswordSuccess({
          changePasswordStatus: EAccountConfirmationStatus.Changed,
        });
      }),
      catchError((errorResult: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, errorResult.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return of(
          changePasswordFailure({
            changePasswordStatus: EAccountConfirmationStatus.Error,
            errorMessage: errMsg,
          })
        );
      })
    );
  }

  private getAuthRequest(requestParameter: string): IAuthRequest {
    const [clientId, grantType, clientSecret, scope] = requestParameter.split(',');
    const request: IAuthRequest = {
      client_id: clientId,
      client_secret: grantType,
      grant_type: clientSecret,
      scope: scope,
    };

    return request;
  }
  private getErrorMessage(statusCode: number): string {
    let errorMessage = '';
    switch (statusCode) {
      case 400:
        errorMessage = this.translate.instant('Common.400_Error');
        break;
      case 401:
        errorMessage = this.translate.instant('Common.401_Error');
        break;
      case 404:
        errorMessage = this.translate.instant('Common.404_Error');
        break;
      case 403:
        errorMessage = this.translate.instant('Common.403_Error');
        break;
      case 500:
        errorMessage = this.translate.instant('Common.500_Error');
        break;
      default:
        errorMessage = this.translate.instant('Common.Failure');
        break;
    }

    return errorMessage;
  }

  private showErrorMessage(errMsg: string): void {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: errMsg,
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
  }
}
